import React from 'react';
import { isHash, shortenId, capitalize, isGuid, addTrailingSlash } from 'Helpers/utils';
import { CustomEnrichmentMethodEnum, LogTransportMethodsEnum, ARN_REGEX } from 'Source/constants';
import { LogPullerTypeEnum } from 'Generated/schema';
import { getUrlWithDefaultParams } from 'Source/urls';
import { useLocation } from 'react-router';
import { IBreadcrumbContext, Crumb } from './types';

const shortenHashId = (text: string) =>
  isHash(text.toLowerCase()) ? shortenId(text).toLowerCase() : text;

export const customPathNames = {
  [LogTransportMethodsEnum.s3]: 'AWS S3',
  [LogTransportMethodsEnum.sqs]: 'AWS SQS',
  [LogTransportMethodsEnum.cloudwatch]: 'AWS CloudWatch Logs',
  [LogTransportMethodsEnum.gcs]: 'Google Cloud Storage',
  [CustomEnrichmentMethodEnum.gcs]: 'Google Cloud Storage',
  [LogTransportMethodsEnum.gcpPubSub]: 'Google Cloud Pub/Sub',
  [LogTransportMethodsEnum.azureBlob]: 'Azure Blob Storage',
  [LogTransportMethodsEnum.http]: 'HTTP Ingest',
  [LogTransportMethodsEnum.otlp]: 'OTLP (OpenTelemetry Protocol)',
  [LogTransportMethodsEnum.azureEventhub]: 'Azure Event Hub',
  [LogPullerTypeEnum.M365]: 'Microsoft 365',
  [LogPullerTypeEnum.Onepassword]: '1Password',
  [LogPullerTypeEnum.Gsuite]: 'Google Workspace',
  [LogPullerTypeEnum.Microsoftgraph]: 'Microsoft Graph',
  [LogPullerTypeEnum.Mongodb]: 'Mongo DB Atlas',
  [LogPullerTypeEnum.SentinelOneCloudFunnel]: 'SentinelOne Cloud Funnel',
  'api-tokens': 'API Tokens',
  'mitre-attack': 'MITRE ATT&CK®',
  googlethreatintelligence: 'Google Threat Intelligence',
  virustotal: 'VirusTotal',
  api: 'API',
  tokens: 'Tokens',
  playground: 'API Playground',
  ai: 'Panther AI',
  'alerts-and-errors': 'Alerts',
  dashboard: 'Dashboards',
  destinations: 'Alert Destinations',
  history: 'Chat History',
  resources: 'Cloud Resources',
};

// Prefixes that group routes but aren't sidebar destinations — the child segment is the nav
// item, so the prefix is hidden from the trail (URLs unchanged; a bare prefix keeps its crumb).
export const hiddenCategorySegments = new Set(['configure', 'investigate', 'build', 'ai']);

export const parseBreadcrumbText = (text: string) =>
  text
    .replace(/([-_])+/g, ' ')
    .replace(/ and /g, ' & ')
    .split(' ')
    .map(capitalize)
    .map(shortenHashId)
    .join(' ');

const generateBreadcrumbText = (pathKey: string) => {
  const uriDecodedPathKey = decodeURIComponent(pathKey);
  const isPathKeyArn = Boolean(uriDecodedPathKey.match(ARN_REGEX));
  const isCustomPath = Object.keys(customPathNames).includes(pathKey);
  const shouldParseBreadcrumb = !isPathKeyArn && !isCustomPath;

  if (isCustomPath) {
    return customPathNames[pathKey];
  }
  if (shouldParseBreadcrumb) {
    return parseBreadcrumbText(uriDecodedPathKey);
  }

  return uriDecodedPathKey;
};

const BreadcrumbContext = React.createContext(null);

const BreadcrumbProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation();
  const [crumbs, setCrumbs] = React.useState([]);

  React.useEffect(() => {
    // split by slash and remove empty-splitted values caused by trailing slashes. We also don't
    // want to display the UUIDs as part of the breadcrumbs (which unfortunately exist in the URL)
    const pathKeys = pathname.split('/').filter(fragment => !!fragment && !isGuid(fragment));
    if (pathKeys.length > 1 && hiddenCategorySegments.has(pathKeys[0])) {
      pathKeys.shift();
    }

    // return the label (what to show) and the uri of each fragment. The URI is constructed by
    // taking the existing path and removing whatever is after each pathKey (only keeping whatever
    // is before-and-including our key). The key is essentially the URL path itself just prettified
    // for display
    const pathnameWithTrailingSlash = addTrailingSlash(pathname);
    const newCrumbs = pathKeys.length
      ? [
          ...pathKeys.map(key => ({
            key,
            href: getUrlWithDefaultParams(
              `${pathnameWithTrailingSlash.substr(0, pathnameWithTrailingSlash.indexOf(`/${key}/`))}/${key}/`, // prettier-ignore
            ),
            children: generateBreadcrumbText(key),
          })),
        ]
      : [];

    setCrumbs(newCrumbs);
  }, [pathname]);

  const addCrumb = React.useCallback(
    (crumb: Crumb, position?: number) => {
      const index = crumbs.findIndex(c => c.key === crumb.key);
      if (index === -1) {
        if (position !== undefined) {
          const crumbsCopy = [...crumbs];
          crumbsCopy.splice(position, 0, crumb);
          setCrumbs(crumbsCopy);
        } else {
          setCrumbs(prevData => [...prevData, crumb]);
        }
      }
    },
    [crumbs],
  );

  /**
   * Remove the crumb for a specific index
   */
  const removeCrumb = React.useCallback(
    (key: string) => {
      const index = crumbs.findIndex(c => c.key === key);
      if (index > 0) {
        const crumbsCopy = [...crumbs];
        crumbsCopy.splice(index, 1);
        setCrumbs(crumbsCopy);
      }
    },
    [crumbs],
  );

  /**
   * Replace the crumb with a new one
   */
  const replaceCrumb = React.useCallback(
    (key: string, newCrumb: Crumb) => {
      const index = crumbs.findIndex(c => c.key === key);
      if (index > 0) {
        const crumbsCopy = [...crumbs];
        crumbsCopy[index] = newCrumb;
        setCrumbs(crumbsCopy);
      }
    },
    [crumbs],
  );

  const contextValue = React.useMemo(
    () => ({ crumbs, addCrumb, removeCrumb, replaceCrumb }),
    [addCrumb, crumbs, removeCrumb, replaceCrumb],
  );

  return <BreadcrumbContext.Provider value={contextValue}>{children}</BreadcrumbContext.Provider>;
};

const useBreadcrumbContext = () => {
  const context = React.useContext(BreadcrumbContext);
  if (context === undefined) {
    throw new Error('useBreadcrumbContext must be used within a BreadcrumbProvider');
  }
  return context as IBreadcrumbContext;
};

export { BreadcrumbProvider, useBreadcrumbContext };
